import { unionBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import { AddCaseCommentFormData } from 'dashboard/forms/FormAddCaseComment';
import { AssignCaseFormData } from 'dashboard/forms/FormAssignCase';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { FormSendCaseEmailData } from 'dashboard/forms/FormSendCaseEmail';
import { FormSetCaseStateData } from 'dashboard/forms/FormSetCaseState';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { NextPageSetting } from 'dashboard/lib/constants/case';
import { reportIssueSuccessMessage } from 'dashboard/lib/constants/reportIssue';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Case, CaseDataManager } from 'dashboard/models/case';
import { CaseComment } from 'dashboard/models/case-comment';
import { CaseMessage } from 'dashboard/models/case-message';
import {
  CASE_QUERY_RESULT_TERMINAL_STATUSES,
  CaseQueryResult,
} from 'dashboard/models/case-query-result';
import { Inquiry } from 'dashboard/models/inquiry';
import { Report, ReportStatus, SUPPORTED_REPORT_TYPES } from 'dashboard/models/report';
import { SAR, SARData } from 'dashboard/models/sar';
import { Verification } from 'dashboard/models/verification';
import {
  WORKFLOW_RUN_TERMINAL_STATES,
  WorkflowRun,
  WorkflowRunStatus,
} from 'dashboard/models/workflow-run';
import { clearMessages, showMessage } from 'dashboard/redux/actions/messages';
import { useListCaseVariableDefinitionsQuery } from 'dashboard/redux/api/case-variable-definition';
import { useListUsersQuery } from 'dashboard/redux/api/users';
import { getCaseGenericDocuments } from 'dashboard/redux/selectors/case';
import { getAllEnabledTemplates } from 'dashboard/redux/selectors/organization';
import {
  clearCase,
  removeCaseComment,
  removeCaseMessage,
  setCase,
  setCaseComment,
  setCaseDataManager,
  setCaseMessage,
  setCaseQueryResults,
  setCaseToken,
} from 'dashboard/redux/slices/case';
import { RootState } from 'dashboard/redux/store';
import { loadAccountStatuses } from 'dashboard/redux/thunk-actions/account-statuses';
import { loadAccountTypes } from 'dashboard/redux/thunk-actions/account-types';
import {
  loadCase,
  loadCaseInquiries,
  loadCaseReports,
  redactCase,
  refreshCaseQueryResults,
} from 'dashboard/redux/thunk-actions/case';
import { refreshCaseQueryResult } from 'dashboard/redux/thunk-actions/case-query-result';
import { flagInquiry } from 'dashboard/redux/thunk-actions/inquiry';
import { updateSAR } from 'dashboard/redux/thunk-actions/sar';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import { flagVerification } from 'dashboard/redux/thunk-actions/verification';
import DashboardCaseShowView from 'dashboard/views/DashboardCaseShowView';
import { LocalStorageKey } from 'lib/clients/StorageClient';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useInterval from 'lib/hooks/useInterval';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiEndpoint, ApiModel, ApiPayload, ApiSingletonPayload } from 'lib/interfaces/api';
import {
  conflictErrorString,
  isConflictError,
  isPermissionsError,
  permissionsErrorMessage,
} from 'lib/permissions-errors';
import { captureException } from 'lib/reporting';
import { Json } from 'lib/types';
import { PreparedFileUpload } from 'lib/utils/dataUrl';
import { assertNever } from 'lib/utils/types';

type Params = { caseId: string };

const DashboardCaseShowRoute = (props: PropsFromRedux) => {
  const { caseId } = useParams<Params>() as Params; // caseId is always defined
  const navigate = useNavigate();
  const refreshCaseQueryOnLoad = useFeatureFlag('dashboard:case-queries-refresh-on-load');
  const enableCaseVariableDefinitons = useFeatureFlag('dashboard:cases-data-context-layer');
  const [hasCasePermissionError, setHasCasePermissionError] = useState<boolean>(false);

  useRedirectOnEnvironmentChange('/dashboard/cases');

  const {
    accountTypes = [],
    actionWorkflows = [],
    assignee,
    caseComments = [],
    caseDataManager,
    caseMessages = [],
    caseQueryResults = [],
    caseTemplate,
    commentWorkflows = [],
    commentWorkflowStepActions = [],
    commentWorkflowRuns = [],
    objects = [],
    caseTags = [],
    tags = [],
    transactionStatuses = [],
    transactionTypes = [],
    verifications = [],
  } = props.caseDetails ?? {};

  const {
    inquiries = [],
    devices = [],
    inquiryTemplateVersions = [],
    inquiryVerifications = [],
    listItems = [],
    listMatches = [],
    lists = [],
    sessions = [],
    tags: inquiryTags = [],
    templates = [],
  } = props.caseDetails?.inquiriesDetails ?? {};

  const {
    reports = [],
    reportTemplates = [],
    tags: reportTags = [],
  } = props.caseDetails?.reportsDetails ?? {};

  // use RTK query to leverage caching
  const { data: users, error: listUsersError } = useListUsersQuery();

  useRTKErrorHandler({
    error: listUsersError,
    onError: (err) => {
      switch (err.status) {
        case 401:
          return {
            message:
              'Your role does not have permissions to view users in this organization. You cannot assign cases.',
            level: MessageType.Notice,
          };
        default:
          return {
            message: 'Failed to load users.',
            level: MessageType.Failure,
          };
      }
    },
  });

  // NB: if there are any changes to the state tags on a case template, we need to fetch all tags to update redux.
  // We need to stringify in order to compare the actual value of the object - otherwise, React will compare by reference and always trigger.
  const stateTags = JSON.stringify(
    caseTemplate?.attributes.configuration['state-tags']
      ?.flatMap((st) => st['available-tags'])
      .sort(),
  );

  useApiLoader({
    loader: () => props.loadAccountStatuses(),
    deps: [props.loadAccountStatuses],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account statuses.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadAccountTypes(),
    deps: [props.loadAccountTypes],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account types.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadTags('all'),
    deps: [props.loadTags, stateTags],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: generateSummaryError, callback: generateSummary } = useApiCallback(
    async (userPrompt: string) => {
      const summaryResponse = await SessionClientDashboard.postResourceAction<Case>(
        { id: caseId, type: ApiModel.Case },
        'generate-ai-summary',
        {
          meta: {
            'user-prompt': userPrompt,
          },
        },
      );

      return summaryResponse?.meta?.response as string;
    },
    [props.case, props.setCaseComment],
  );

  useApiErrorHandler({
    error: generateSummaryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to generate summary.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const [nextPageSettingDefault, setNextPageSettingDefault] = useLocalStorage<NextPageSetting>(
    LocalStorageKey.DashboardCasesNextPageSettingDefault,
    NextPageSetting.StayOnCurrentCase,
  );

  const { loading, callback: loadCase } = useApiLoader({
    loader: () => {
      setHasCasePermissionError(false);
      props.setCaseToken({ key: caseId });
      return props.loadCase({
        id: caseId,
        currentEnvironment: props.currentEnvironment,
        currentUser: props.currentUser,
      });
    },
    deps: [props.setCaseToken, props.loadCase, caseId, props.currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        setHasCasePermissionError(true);
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: pollCaseForPendingReports, error: pollCaseForPendingReportsError } =
    useApiCallback(async () => {
      const toProcessReports = reports?.filter((object: Report) =>
        SUPPORTED_REPORT_TYPES.includes(object.type),
      );
      const hasPending = toProcessReports.some(
        (report: Report) => report.attributes?.status === ReportStatus.Pending,
      );
      if (hasPending) {
        props.loadCaseReports({
          id: caseId,
          isPollRequest: true,
        });
      }
    }, [reports, props.loadCaseReports, caseId]);

  useApiErrorHandler({
    error: pollCaseForPendingReportsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to refresh case.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useInterval({
    callback: pollCaseForPendingReports as (...args: unknown[]) => Promise<void>,
    delay: 5000,
    pauseWhenBackgrounded: true,
  });

  // -----------------------------------------------------------------------------------------
  // START: Load case objects individually
  // -----------------------------------------------------------------------------------------
  //
  // NOTE: these *should* be moved to the individual modules as we move to a pattern where modules
  // request for their own data. For now, we keep the loaders in a single place for migration
  // simplicity and speed of responding to case slow load time requests.
  const { loading: isLoadingInquiriesDetails, callback: loadCaseInquiries } = useApiLoader({
    loader: () => props.loadCaseInquiries({ id: caseId }),
    deps: [props.loadCaseInquiries, caseId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case inquiries.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingReportsDetails, callback: loadCaseReports } = useApiLoader({
    loader: () => props.loadCaseReports({ id: caseId }),
    deps: [props.loadCaseReports, caseId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case reports.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });
  // -----------------------------------------------------------------------------------------
  // END: Load case objects individually
  // -----------------------------------------------------------------------------------------

  const { error: redactCaseError, callback: handleRedactCase } = useApiCallback(async () => {
    await props.redactCase(caseId);
    await loadCase();
    props.showMessage('Successfully redacted case.', MessageType.Success);
  }, [props.case, props.redactCase, loadCase]);

  useApiErrorHandler({
    error: redactCaseError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to redact case, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to redact case, please try again.', MessageType.Failure),
  });

  const { error: addCommentError, callback: addComment } = useApiCallback(
    async ({ message }: AddCaseCommentFormData, mentionedUsers: string[]) => {
      const commentResponse = await SessionClientDashboard.postResourceCreate<
        CaseComment,
        ApiSingletonPayload<
          {
            message: string;
            'mentioned-users': string[];
            'case-id': string;
          },
          ApiModel.CaseComment
        >
      >({
        attributes: {
          message: message,
          'mentioned-users': mentionedUsers,
          'case-id': caseId,
        },
        type: ApiModel.CaseComment as const,
      });
      props.setCaseComment({ comment: commentResponse.data, key: caseId });
    },
    [props.case, props.setCaseComment],
  );

  useApiErrorHandler({
    error: addCommentError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to add comment, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to add comment, please try again.', MessageType.Failure, false),
  });

  const { error: deleteCaseCommentError, callback: deleteCaseComment } = useApiCallback(
    async (id: string) => {
      await SessionClientDashboard.deleteResource<CaseComment>(ApiEndpoint.CaseComment, id);
      props.removeCaseComment({ id, key: caseId });
    },
    [props.removeCaseComment],
  );

  useApiErrorHandler({
    error: deleteCaseCommentError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to delete comment, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to delete comment, please try again.', MessageType.Failure, false),
  });

  const { error: editCommentError, callback: editComment } = useApiCallback(
    async (message: string, id: string, mentionedUsers: string[]) => {
      const commentResponse = await SessionClientDashboard.updateResource<
        CaseComment,
        ApiPayload<{ message: string; 'mentioned-users': string[] }>
      >({
        type: ApiModel.CaseComment,
        id: id,
        attributes: {
          message: message,
          'mentioned-users': mentionedUsers,
        },
      });
      props.setCaseComment({ comment: commentResponse.data, key: caseId });
    },
    [props.case, props.setCaseComment],
  );

  useApiErrorHandler({
    error: editCommentError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to edit comment, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to edit comment, please try again.', MessageType.Failure, false),
  });

  const { error: assignCaseError, callback: assignCase } = useApiCallback(
    async ({ userId, message }: AssignCaseFormData) => {
      // If we are passed an undefined userId, we take that to mean unassigned.
      // To prevent axios from stripping that from the payload, we need to set it to null
      // See https://github.com/axios/axios/issues/1139
      const assigneeId = userId != null ? userId : null;
      await SessionClientDashboard.postResourceAction<Case>(
        {
          type: ApiModel.Case,
          id: caseId,
        },
        'assign',
        {
          meta: {
            'assignee-id': assigneeId,
            message: message,
          },
        },
      );
      await loadCase();
      props.showMessage('Successfully assigned case.', MessageType.Success, true);
    },
    [props.case, props.setCase],
  );

  useApiErrorHandler({
    error: assignCaseError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to assign case, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to assign case, please try again.', MessageType.Failure, false),
  });

  const { error: setCaseStateError, callback: handleSetCaseState } = useApiCallback(
    async (stateId: string, { tags, message, nextPageSetting }: FormSetCaseStateData) => {
      const allTags = [...Array(tags).flat(), ...(caseTags || []).map((tag) => tag.id)];
      const [{ meta: kaseMeta }] = await Promise.all([
        SessionClientDashboard.postResourceAction<Case>(
          {
            type: ApiModel.Case,
            id: caseId,
          },
          'set-state',
          {
            meta: {
              'state-id': stateId,
              message: message,
            },
          },
        ),
        ...(tags && tags.length > 0
          ? [
              SessionClientDashboard.postResourceAction<Case>(
                {
                  type: ApiModel.Case,
                  id: caseId,
                },
                'set-tags',
                { meta: { 'tag-id': allTags } },
              ),
            ]
          : []),
      ]);

      switch (nextPageSetting) {
        case NextPageSetting.GoToNextCase:
          setNextPageSettingDefault(NextPageSetting.GoToNextCase);
          if (kaseMeta?.['next-open-case-id']) {
            props.showMessage(
              'Successfully updated case status. Going to next case.',
              MessageType.Success,
              true,
            );
            navigate(`/dashboard/cases/${kaseMeta['next-open-case-id']}`);
          } else {
            props.showMessage(
              'Successfully updated case status. No more cases of this type assigned to you.',
              MessageType.Success,
              true,
            );
            navigate('/dashboard/cases');
          }
          break;
        case NextPageSetting.GoToCasesHome:
          setNextPageSettingDefault(NextPageSetting.GoToCasesHome);
          props.showMessage('Successfully updated case status.', MessageType.Success, true);
          navigate('/dashboard/cases');
          break;
        case NextPageSetting.StayOnCurrentCase:
          setNextPageSettingDefault(NextPageSetting.StayOnCurrentCase);
          await loadCase();
          props.showMessage('Successfully updated case status.', MessageType.Success, true);
          break;
        default:
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          assertNever(nextPageSetting);
      }
    },
    [props.case, caseTags, props.setCase],
  );

  useApiErrorHandler({
    error: setCaseStateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 403) {
        props.showMessage(
          'Failed to update case status, please complete the checklist first.',
          MessageType.Failure,
        );
      } else {
        captureException(error);
        props.showMessage(
          'Failed to update case status, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to set case status, please try again.', MessageType.Failure, false),
  });

  const { callback: handleToggleChecklistItem, error: handleToggleChecklistItemError } =
    useApiCallback(
      async (itemToken: string) => {
        const kaseResponse = await SessionClientDashboard.postResourceAction<Case>(
          {
            type: ApiModel.Case,
            id: caseId,
          },
          'toggle-checklist-item',
          { meta: { 'checklist-item-id': itemToken } },
        );

        props.setCase({ key: caseId, case: kaseResponse.data });
        if (kaseResponse.meta?.['case-data-manager']) {
          props.setCaseDataManager({
            caseDataManager: kaseResponse.meta['case-data-manager'] as CaseDataManager,
            key: caseId,
          });
        }
      },
      [props.case, loadCase],
    );

  useApiErrorHandler({
    error: handleToggleChecklistItemError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage(
          'Failed to update case checklist, please try again.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to update case checklist, please try again.', MessageType.Failure),
  });

  const { callback: handleUpdateFields, error: handleUpdateFieldsError } = useApiCallback(
    async (
      fieldsToUpdate: Record<string, any>,
      { showToast = true }: { showToast?: boolean } = {},
    ) => {
      const caseResponse = await SessionClientDashboard.updateResource<Case>({
        attributes: {
          fields: fieldsToUpdate,
        },
        id: caseId,
        type: ApiModel.Case,
      });
      props.setCase({ case: caseResponse.data, key: caseId });

      if (caseResponse.meta?.['case-data-manager']) {
        props.setCaseDataManager({
          caseDataManager: caseResponse.meta['case-data-manager'] as CaseDataManager,
          key: caseId,
        });
      }

      if (showToast) {
        props.showMessage('Successfully updated case fields.', MessageType.Success, true);
      }
    },
    [props.case, props.setCase, props.setCaseDataManager],
  );

  useApiErrorHandler({
    error: handleUpdateFieldsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (
        (error.status === 422 && error.errors?.[0].title === 'Record invalid') ||
        (error.status === 400 && error.errors?.[0].title === 'Bad request')
      ) {
        props.showMessage(
          'Failed to update case fields, please ensure field values are valid and try again.',
          MessageType.Failure,
        );
      } else {
        captureException(error);
        props.showMessage('Failed to update case fields, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to update case fields, please try again.', MessageType.Failure),
  });

  const { callback: handleAddCaseAttachment, error: handleAddCaseAttachmentError } = useApiCallback(
    async (attachments: PreparedFileUpload[]) => {
      await SessionClientDashboard.postResourceAction<Case>(
        {
          type: ApiModel.Case,
          id: caseId,
        },
        'add-attachments',
        {
          contentType: 'multipart/form-data',
          meta: { attachments: attachments },
        },
      );
      await loadCase();
      props.showMessage('Successfully uploaded attachment.', MessageType.Success);
    },
    [props.case, loadCase],
  );

  useApiErrorHandler({
    error: handleAddCaseAttachmentError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to add case attachment.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to add case attachment.', MessageType.Failure),
  });

  const { callback: handleDeleteCaseAttachment, error: handleDeleteCaseAttachmentError } =
    useApiCallback(
      async (attachmentKey: string) => {
        await SessionClientDashboard.postResourceAction<Case>(
          {
            type: ApiModel.Case,
            id: caseId,
          },
          'delete-attachment',
          {
            meta: { 'attachment-key': attachmentKey },
          },
        );
        await loadCase();
        props.showMessage('Successfully deleted attachment.', MessageType.Success);
      },
      [props.case, loadCase],
    );

  useApiErrorHandler({
    error: handleDeleteCaseAttachmentError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (isConflictError(error)) {
        props.showMessage(conflictErrorString(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to delete case attachment.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to delete case attachment.', MessageType.Failure),
  });

  const { callback: handleUpdateTags, error: handleUpdateTagsError } = useApiCallback(
    async ({ tags: tagIds }: FormUpdateTagsData) => {
      await SessionClientDashboard.postResourceAction<Case>(
        {
          type: ApiModel.Case,
          id: caseId,
        },
        'set-tags',
        { meta: { 'tag-id': tagIds } },
      );

      await loadCase();
    },
    [props.case, loadCase],
  );

  useApiErrorHandler({
    error: handleUpdateTagsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to add tags to inquiry.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to add tags to inquiry.', MessageType.Failure),
  });

  const { callback: handleUpdateInquiryTags, error: handleUpdateInquiryTagsError } = useApiCallback(
    async (inquiryId: string, tags: string[]) => {
      await SessionClientDashboard.postResourceAction<Inquiry>(
        {
          type: ApiModel.Inquiry,
          id: inquiryId,
        },
        'set-tags',
        { meta: { 'tag-id': tags } },
      );

      await loadCaseInquiries();
    },
    [loadCaseInquiries],
  );

  useApiErrorHandler({
    error: handleUpdateInquiryTagsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to add tags to inquiry.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to add tags to inquiry.', MessageType.Failure),
  });

  const { callback: handleUpdateReportTags, error: handleUpdateReportTagsError } = useApiCallback(
    async (reportId: string, tags: string[]) => {
      await SessionClientDashboard.postResourceAction<Report>(
        {
          type: ApiModel.Report,
          id: reportId,
        },
        'set-tags',
        { meta: { 'tag-id': tags } },
      );

      await loadCaseReports();
    },
    [loadCaseReports],
  );

  useApiErrorHandler({
    error: handleUpdateReportTagsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to add tags to report.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to add tags to report.', MessageType.Failure),
  });

  const { callback: handleCreateEmailDraft, error: handleCreateEmailDraftError } = useApiCallback(
    async ({
      to,
      from,
      body,
      threadId,
    }: {
      to: string[];
      from: string;
      body: string;
      threadId?: string;
    }) => {
      const caseMessageResponse = await SessionClientDashboard.postResourceCreate<CaseMessage>(
        {
          type: ApiModel.CaseMessage,
          attributes: {
            mail: {
              to,
              from,
              body,
            },
          },
        },
        {
          meta: { 'case-id': caseId, ...(threadId && { 'thread-id': threadId }) },
        },
      );
      props.setCaseMessage({
        caseMessage: caseMessageResponse.data,
        key: caseId,
      });
    },
    [props.case, props.setCaseMessage],
  );

  useApiErrorHandler({
    error: handleCreateEmailDraftError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (isConflictError(error)) {
        props.showMessage(conflictErrorString(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to create email.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to create email.', MessageType.Failure),
  });

  const { callback: handleUpdateEmailDraft, error: handleUpdateEmailDraftError } = useApiCallback(
    async ({
      id,
      'from-email-address': from,
      'to-email-addresses': to,
      'cc-email-addresses': cc,
      subject,
      'body-text': body,
      'last-updated-at': lastUpdatedAt,
      'related-objects': relatedObjects,
    }: FormSendCaseEmailData) => {
      props.clearMessages();
      const caseMessageResponse = await SessionClientDashboard.updateResource<CaseMessage>(
        {
          type: ApiModel.CaseMessage,
          id,
          attributes: {
            mail: {
              from,
              to,
              cc,
              subject,
              body,
            },
          },
        },
        { meta: { 'last-updated-at': lastUpdatedAt, 'related-objects': relatedObjects } },
      );

      props.setCaseMessage({
        caseMessage: caseMessageResponse.data,
        key: caseId,
      });
      props.showMessage('Successfully updated email draft.', MessageType.Success);
    },
    [props.case, props.setCaseMessage],
  );

  useApiErrorHandler({
    error: handleUpdateEmailDraftError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error instanceof ApiResponseError && error.status === 400) {
        props.showMessage(
          'Failed to update email draft, please refresh and try again.',
          MessageType.Failure,
        );
      } else {
        captureException(error);
        props.showMessage('Failed to update email draft', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to update email draft', MessageType.Failure),
  });

  const { callback: handleSendEmail, error: handleSendEmailError } = useApiCallback(
    async ({
      id,
      'from-email-address': from,
      'to-email-addresses': to,
      'cc-email-addresses': cc,
      subject,
      'body-text': body,
      'last-updated-at': lastUpdatedAt,
      'related-objects': relatedObjects,
    }: FormSendCaseEmailData) => {
      props.clearMessages();
      const caseMessageResponse = await SessionClientDashboard.postResourceAction<CaseMessage>(
        {
          type: ApiModel.CaseMessage,
          id,
          attributes: {
            mail: {
              from,
              to,
              cc,
              subject,
              body,
            },
          },
        },
        'update-and-send',
        { meta: { 'last-updated-at': lastUpdatedAt, 'related-objects': relatedObjects } },
      );

      props.setCaseMessage({
        caseMessage: caseMessageResponse.data,
        key: caseId,
      });
      props.showMessage('Successfully sent email.', MessageType.Success);
    },
    [props.case, props.setCaseMessage],
  );

  useApiErrorHandler({
    error: handleSendEmailError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error instanceof ApiResponseError && error.status === 400) {
        props.showMessage(
          'Failed to send email, please refresh and try again.',
          MessageType.Failure,
        );
      } else {
        captureException(error);
        props.showMessage('Failed to send email', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to send email.', MessageType.Failure),
  });

  const { callback: handleDeleteEmail, error: handleDeleteEmailError } = useApiCallback(
    async (id: string) => {
      props.clearMessages();
      await SessionClientDashboard.deleteResource<CaseMessage>(ApiEndpoint.CaseMessage, id);
      props.removeCaseMessage({ id, key: caseId });
      props.showMessage('Successfully deleted case message draft.', MessageType.Success);
    },
    [props.case, props.removeCaseMessage],
  );

  useApiErrorHandler({
    error: handleDeleteEmailError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error instanceof ApiResponseError && error.status === 400) {
        props.showMessage(
          'Failed to delete email, please refresh and try again.',
          MessageType.Failure,
        );
      } else {
        captureException(error);
        props.showMessage('Failed to delete email', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to delete email.', MessageType.Failure),
  });

  // -----------------------------------------------------------------------------------------
  // Custom case actions
  // -----------------------------------------------------------------------------------------
  let pollWorkflowRun: number;
  const POLL_INTERVAL = 1000;
  const MAX_TIMEOUT = 10;
  const pollForWorkflowRunComplete = async (maxTimeOut: number, workflowRunId: string) => {
    if (maxTimeOut < 0) {
      // Stop polling if workflow is not transitioned into terminal state in 10 sec
      clearTimeout(pollWorkflowRun);
      return;
    }

    const pendingWorkflowRun = (
      await SessionClientDashboard.getResource<WorkflowRun>(ApiEndpoint.WorkflowRun, workflowRunId)
    ).data;

    if (WORKFLOW_RUN_TERMINAL_STATES.includes(pendingWorkflowRun.attributes.status)) {
      clearTimeout(pollWorkflowRun);
      await Promise.all([loadCase(), loadCaseInquiries(), loadCaseReports()]);

      if (pendingWorkflowRun.attributes.status === WorkflowRunStatus.Completed) {
        props.showMessage('Action successfully completed.', MessageType.Success);
      } else {
        props.showMessage('Action failed to run.', MessageType.Failure);
      }
    } else {
      pollWorkflowRun = window.setTimeout(() => {
        pollForWorkflowRunComplete(maxTimeOut - 1, workflowRunId);
      }, POLL_INTERVAL);
    }
  };

  const { callback: handleRunCaseActionWorkflow, error: handleRunCaseActionWorkflowError } =
    useApiCallback(
      async (values: Json, workflowId: string) => {
        const response = await SessionClientDashboard.postResourceAction<Case>(
          {
            type: ApiModel.Case,
            id: caseId,
          },
          'run-action',
          {
            meta: {
              'workflow-id': workflowId,
              'trigger-params': values,
            },
          },
        );

        props.showMessage('Started running action in the background.', MessageType.Success);

        if (response.meta?.['workflow-run-id']) {
          await pollForWorkflowRunComplete(MAX_TIMEOUT, response.meta['workflow-run-id']);
        }
      },
      [props.case],
    );

  useApiErrorHandler({
    error: handleRunCaseActionWorkflowError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        // don't show anything if this is a permission error since the action is likely running,
        // we just can't poll the endpoint to confirm.
      } else {
        captureException(error);
        props.showMessage('Failed to run action', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to run action', MessageType.Failure),
  });

  const { error: addTagsError, callback: handleAddTags } = useApiCallback(
    async (accountIds: string[], { tags: tagIds }: FormUpdateTagsData) => {
      await SessionClientDashboard.postResourceSingletonAction(
        { type: ApiModel.Account },
        'bulk-add-tags',
        {
          meta: {
            account_ids: accountIds,
            tag_id: tagIds,
          },
        },
      );

      const displayMessage = `Successfully added ${tagIds.length} ${
        tagIds.length > 1 ? 'tags' : 'tag'
      }`;
      props.showMessage(displayMessage, MessageType.Success);
    },
    [],
  );

  useApiErrorHandler({
    error: addTagsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to add tags, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  /************************************************
   * START Bulk update account status handler
   ************************************************/
  const { error: bulkUpdateAccountStatusError, callback: handleBulkUpdateAccountStatus } =
    useApiCallback(async (accountIds: string[], accountStatusId: string) => {
      await SessionClientDashboard.postResourceSingletonAction(
        { type: ApiModel.Account },
        'bulk-update-account-status',
        {
          meta: {
            account_ids: accountIds,
            account_status_id: accountStatusId,
          },
        },
      );

      const displayMessage = `Successfully updated status of ${accountIds.length} accounts`;
      props.showMessage(displayMessage, MessageType.Success);
    }, []);

  useApiErrorHandler({
    error: bulkUpdateAccountStatusError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update status for accounts, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });
  /************************************************
   * END Bulk update account status handler
   ************************************************/

  const { error: handleFlagInquiryIssuesError, callback: handleFlagInquiryIssues } = useApiCallback(
    async (inquiry: Inquiry, verifications: Verification[], values: FlagIssuesFormData) => {
      const { issueVerifications, issueType, responseType, content, addAsFraudulent } = values;

      await props.flagInquiry(inquiry, verifications, {
        issueVerifications,
        issueType,
        responseType,
        content,
        addAsFraudulent,
      });
      props.showMessage(reportIssueSuccessMessage(responseType), MessageType.Success);
    },
    [props.flagInquiry],
  );

  useApiErrorHandler({
    error: handleFlagInquiryIssuesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to report issue, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to report issue, please try again.', MessageType.Failure),
  });

  const { error: handleFlagVerificationIssuesError, callback: handleFlagVerificationIssues } =
    useApiCallback(
      async (verification: Verification, values: FlagIssuesFormData) => {
        const { issueVerifications, issueType, responseType, content, addAsFraudulent } = values;
        await props.flagVerification(verification, {
          issueVerifications,
          issueType,
          responseType,
          content,
          addAsFraudulent,
        });
        props.showMessage(reportIssueSuccessMessage(responseType), MessageType.Success);
      },
      [props.flagVerification],
    );

  useApiErrorHandler({
    error: handleFlagVerificationIssuesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to report issue, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to report issue, please try again.', MessageType.Failure),
  });

  const { error: createSARError, callback: handleCreateSAR } = useApiCallback(async () => {
    await SessionClientDashboard.postResourceCreate<
      SAR,
      ApiSingletonPayload<{
        'environment-id': string;
        'case-id': string;
      }>
    >({
      type: ApiModel.SAR,
      attributes: {
        'environment-id': props.currentEnvironment.id,
        'case-id': caseId,
      },
    });
    props.showMessage('Successfully created a SAR.', MessageType.Success);
    await loadCase();
  }, [props.showMessage, props.currentEnvironment, props.case]);

  useApiErrorHandler({
    error: createSARError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to create SAR, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: updateSARDataError, callback: handleUpdateSAR } = useApiCallback(
    async (sarId: string, sarData: SARData) => {
      await props.updateSAR(sarId, { data: sarData });
      props.showMessage('Successfully updated SAR.', MessageType.Success, false);
    },
    [props.updateSAR, props.showMessage],
  );

  useApiErrorHandler({
    error: updateSARDataError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to update SAR, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to update SAR, please try again.', MessageType.Failure, false),
  });

  const { error: autofillSARError, callback: handleAutofillSAR } = useApiCallback(
    async (sarId: string) => {
      await SessionClientDashboard.postResourceAction<SAR>(
        {
          type: ApiModel.SAR,
          id: sarId,
        },
        'autofill-attached-objects',
      );
      await loadCase();
    },
    [props.updateSAR, props.showMessage, loadCase],
  );

  useApiErrorHandler({
    error: autofillSARError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error);
        props.showMessage('Failed to autofill SAR, please try again.', MessageType.Failure, false);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to autofill SAR, please try again.', MessageType.Failure, false),
  });

  /************************************************
   * CaseQuery functions
   ************************************************/

  const pollForCaseQueryResult = useCallback(
    (caseQueryResult: CaseQueryResult) => {
      let timer: NodeJS.Timeout | null = null;

      const poll = async () => {
        const response = await SessionClientDashboard.getResource<CaseQueryResult>(
          ApiEndpoint.CaseQueryResult,
          caseQueryResult.id,
        );

        props.setCaseQueryResults({
          caseQueryResults: [response.data],
          key: caseId,
        });

        if (CASE_QUERY_RESULT_TERMINAL_STATUSES.includes(response.data.attributes.status)) {
          clearInterval(pollCaseQueryResultInterval);

          if (timer) {
            clearTimeout(timer);
          }
        }
      };

      const pollCaseQueryResultInterval = setInterval(poll, 1000);

      // Poll for up to 10 seconds
      timer = setTimeout(() => {
        clearInterval(pollCaseQueryResultInterval);
        showMessage(
          'Case query timed out after 30 seconds. Please refresh to try again, or shorten the runtime of your query.',
        );
      }, 30000);
      return pollCaseQueryResultInterval;
    },
    [props],
  );

  const { error: refreshCaseQueryResultError, callback: handleRefreshCaseQueryResult } =
    useApiCallback(
      async (caseQueryResult: CaseQueryResult) => {
        await props.refreshCaseQueryResult(caseId, caseQueryResult);
        pollForCaseQueryResult(caseQueryResult);
      },
      [props.refreshCaseQueryResult],
    );

  useApiErrorHandler({
    error: refreshCaseQueryResultError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error instanceof ApiResponseError && error.status === 401) {
        // swallow unauthorized error coming from impersonation with read-only mode
        return;
      }
    },
    onNetworkError: () => props.showMessage('Failed to rerun case query.', MessageType.Failure),
  });

  /************************************************
   * Refresh Case Query Results on loading
   ************************************************/
  const { error: refreshQueryResultError, callback: handleRefreshCaseQueryResults } =
    useApiCallback(async () => {
      await props.refreshCaseQueryResults(caseId);
    }, [caseId, props.refreshCaseQueryResults]);

  useApiErrorHandler({
    error: refreshQueryResultError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error instanceof ApiResponseError && error.status === 401) {
        // swallow unauthorized error coming from impersonation with read-only mode
        return;
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to refresh case query results, please try again.',
        MessageType.Failure,
      ),
  });

  useEffect(() => {
    if (refreshCaseQueryOnLoad) {
      handleRefreshCaseQueryResults();
    }
  }, [refreshCaseQueryOnLoad, handleRefreshCaseQueryResults]);

  const pollForCaseQueryResults = useCallback(() => {
    const poll = async () => {
      const caseQueryResultsResponse = await SessionClientDashboard.listResource<
        CaseQueryResult,
        { 'case-id': string }
      >(ApiEndpoint.CaseQueryResult, {
        filter: {
          'case-id': caseId,
        },
      });

      props.setCaseQueryResults({
        caseQueryResults: caseQueryResultsResponse.data,
        key: caseId,
      });

      const allCaseQueryInTerminalStates = caseQueryResultsResponse.data.every((result) =>
        CASE_QUERY_RESULT_TERMINAL_STATUSES.includes(result.attributes.status),
      );

      if (allCaseQueryInTerminalStates) {
        clearInterval(pollCaseQueryResultsInterval);
      }
    };

    const pollCaseQueryResultsInterval = setInterval(poll, 1000);
    return pollCaseQueryResultsInterval;
  }, [props]);

  /************************************************
   * Polling Case Query Results
   ************************************************/
  // TODO: only poll case query results that being used in some table modules
  useEffect(() => {
    if (caseQueryResults.length === 0 || !refreshCaseQueryOnLoad) {
      return;
    }

    const pollCaseQueryResultsInterval = pollForCaseQueryResults();

    return () => clearInterval(pollCaseQueryResultsInterval);
  }, [caseQueryResults.length]);

  const { error: variableDefinitionsListQueryError, data: caseVariableDefinitions = [] } =
    useListCaseVariableDefinitionsQuery(caseTemplate?.id, {
      skip: !caseTemplate?.id || !enableCaseVariableDefinitons,
    });

  useRTKErrorHandler({
    error: variableDefinitionsListQueryError,
    onError: () => ({ message: 'Failed to load case queries' }),
  });

  // NOTE: this is a temporary solution as we slowly pull out individual object requests from the
  // main cases includes. The ideal end state is to have each module be responsible for pulling its
  // own data instead of pulling it all here in DashboardCaseShowRoute.
  const caseObjects = unionBy(objects || [], inquiries, reports, 'id');
  const allVerifications = unionBy(verifications, inquiryVerifications, 'id');

  return (
    <DashboardCaseShowView
      case={props.case}
      caseDataManager={caseDataManager}
      caseTemplate={caseTemplate}
      caseQueryResults={caseQueryResults}
      caseVariableDefinitions={caseVariableDefinitions}
      accountStatuses={props.accountStatuses}
      accountTypes={accountTypes}
      assignee={assignee}
      objects={caseObjects}
      users={users ?? []}
      currentEnvironment={props.currentEnvironment}
      templates={templates}
      enabledTemplates={props.enabledTemplates}
      inquiryTemplateVersions={inquiryTemplateVersions}
      actionWorkflows={actionWorkflows}
      inquiryVerifications={inquiryVerifications}
      verifications={allVerifications}
      devices={devices}
      documents={props.documents}
      caseComments={caseComments}
      caseMessages={caseMessages}
      commentWorkflows={commentWorkflows}
      commentWorkflowStepActions={commentWorkflowStepActions}
      commentWorkflowRuns={commentWorkflowRuns}
      reportTemplates={reportTemplates}
      sessions={sessions}
      // There is very awkward naming going on here...we should probably split this into
      // caseTags, inquiryTags, reportTags, etc. but right now we put it all together and it's
      // tricky to figure out which is used where.
      tags={unionBy(tags, caseTags, inquiryTags, reportTags, 'id')}
      transactionStatuses={transactionStatuses}
      transactionTypes={transactionTypes}
      allTags={props.allTags}
      lists={lists}
      listMatches={listMatches}
      listItems={listItems}
      nextPageSettingDefault={nextPageSettingDefault}
      isLoading={loading}
      isLoadingInquiriesDetails={isLoadingInquiriesDetails}
      isLoadingReportsDetails={isLoadingReportsDetails}
      hasCasePermissionError={hasCasePermissionError}
      featureFlags={props.featureFlags}
      addComment={addComment}
      editCaseComment={editComment}
      removeCaseComment={deleteCaseComment}
      assignCase={assignCase}
      handleRefreshCaseQueryResult={handleRefreshCaseQueryResult}
      handleToggleChecklistItem={handleToggleChecklistItem}
      handleRunCaseActionWorkflow={handleRunCaseActionWorkflow}
      handleUpdateFields={handleUpdateFields}
      handleUpdateTags={handleUpdateTags}
      handleUpdateInquiryTags={handleUpdateInquiryTags}
      handleUpdateReportTags={handleUpdateReportTags}
      handleSetCaseState={handleSetCaseState}
      handleAddTags={handleAddTags}
      handleAddCaseAttachment={handleAddCaseAttachment}
      handleDeleteCaseAttachment={handleDeleteCaseAttachment}
      handleBulkUpdateAccountStatus={handleBulkUpdateAccountStatus}
      handleFlagInquiryIssues={handleFlagInquiryIssues}
      handleFlagVerificationIssues={handleFlagVerificationIssues}
      handleCreateEmailDraft={handleCreateEmailDraft}
      handleUpdateEmailDraft={handleUpdateEmailDraft}
      handleSendEmail={handleSendEmail}
      handleDeleteEmail={handleDeleteEmail}
      handleRedactCase={handleRedactCase}
      handleCreateSAR={handleCreateSAR}
      handleUpdateSAR={handleUpdateSAR}
      handleAutofillSAR={handleAutofillSAR}
      handleGenerateSummary={generateSummary}
      showMessage={props.showMessage}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentUser: state.currentUser.currentUser,
    accountStatuses: state.accountStatuses.accountStatuses,
    allTags: state.tags.tags.all,
    case: state.case.caseToken ? state.case.case[state.case.caseToken].case : undefined,
    caseDetails: state.case.caseToken
      ? state.case.case[state.case.caseToken].caseDetails
      : undefined,
    // For now only support generic documents in this component.
    documents: getCaseGenericDocuments(state),
    enabledTemplates: getAllEnabledTemplates(state),
    featureFlags: state.featureFlags.featureFlags,
  };
};

const mapDispatchToProps = {
  loadAccountStatuses,
  loadAccountTypes,
  setCaseQueryResults,
  loadTags,
  clearCase,
  setCase,
  setCaseToken,
  setCaseDataManager,
  loadCase,
  loadCaseInquiries,
  loadCaseReports,
  redactCase,
  refreshCaseQueryResult,
  refreshCaseQueryResults,
  updateSAR,
  flagInquiry,
  flagVerification,
  setCaseComment,
  removeCaseComment,
  setCaseMessage,
  removeCaseMessage,
  clearMessages,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardCaseShowRoute);
